
import React, { useState, useMemo } from 'react';
import { AppState, BetOutcome } from '../types.ts';
import StatsCards from './StatsCards.tsx';
import ProfitChart from './Charts/ProfitChart.tsx';
import OutcomePieChart from './Charts/OutcomePieChart.tsx';
import MarketBarChart from './Charts/MarketBarChart.tsx';
import LeagueBarChart from './Charts/LeagueBarChart.tsx';
import AddBetModal from './AddBetModal.tsx';
import { PlusCircle, TrendingUp, Calendar, SearchX, Activity } from 'lucide-react';

interface DashboardProps {
  state: AppState;
  onAddBet: (bet: any) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ state, onAddBet }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState<'day' | 'week' | 'month' | 'custom'>('day');

  const [startDate, setStartDate] = useState(() => {
    // Definindo o filtro inicial como Hoje
    return new Date().toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const handleSetFilter = (type: 'day' | 'week' | 'month') => {
    setFilterType(type);
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    let start = new Date();
    if (type === 'day') start = now;
    else if (type === 'week') start.setDate(now.getDate() - 7);
    else if (type === 'month') start.setMonth(now.getMonth() - 1);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end);
  };

  const filteredBets = useMemo(() => {
    return state.bets.filter(bet => bet.date >= startDate && bet.date <= endDate);
  }, [state.bets, startDate, endDate]);

  const marketPerformance = useMemo(() => {
    const stats: Record<string, { name: string, profit: number, count: number }> = {};
    filteredBets.forEach(bet => {
      const m = state.markets.find(mk => mk.id === bet.market_id);
      if (m) {
        if (!stats[bet.market_id]) stats[bet.market_id] = { name: m.name, profit: 0, count: 0 };
        stats[bet.market_id].profit += (bet.profit || 0);
        stats[bet.market_id].count += 1;
      }
    });
    return Object.values(stats).filter(m => m.count > 0).sort((a, b) => b.profit - a.profit);
  }, [filteredBets, state.markets]);

  const leaguePerformance = useMemo(() => {
    const stats: Record<string, { name: string, profit: number, count: number }> = {};
    filteredBets.forEach(bet => {
      const l = state.leagues.find(lg => lg.id === bet.league_id);
      if (l) {
        if (!stats[bet.league_id]) stats[bet.league_id] = { name: l.name, profit: 0, count: 0 };
        stats[bet.league_id].profit += (bet.profit || 0);
        stats[bet.league_id].count += 1;
      }
    });
    return Object.values(stats).filter(l => l.count > 0).sort((a, b) => b.profit - a.profit);
  }, [filteredBets, state.leagues]);

  const formatDateDisplay = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="space-y-6 relative flex flex-col min-h-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black uppercase tracking-tight text-white flex items-center gap-2">
            <div className="w-2 h-6 bg-[#00ff66] rounded-full shadow-[0_0_10px_#00ff66]"></div>
            Dashboard Operacional
          </h2>
          <p className="text-[#848e9c] text-[10px] font-black mt-1 uppercase tracking-widest">
            FifaLytics System • <span className="text-[#00ff66] animate-pulse">Live Stats</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 bg-[#1e2329] border border-[#474d57] px-5 py-3 rounded-2xl shadow-xl relative overflow-hidden group hover:border-[#00ff66]/50 transition-all">
            <label
              className="flex items-center gap-3 relative h-6 min-w-[100px] justify-center cursor-pointer"
              onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
            >
              <Calendar size={16} className="text-[#00ff66] pointer-events-none" />
              <span className="text-white text-[11px] font-black tracking-widest pointer-events-none">{formatDateDisplay(startDate)}</span>
              <input
                type="date"
                value={startDate}
                onChange={e => { setStartDate(e.target.value); setFilterType('custom'); }}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </label>
            <span className="text-white text-[10px] font-black uppercase tracking-widest opacity-40 pointer-events-none">até</span>
            <label
              className="flex items-center gap-3 relative h-6 min-w-[100px] justify-center cursor-pointer"
              onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
            >
              <Calendar size={16} className="text-[#00ff66] pointer-events-none" />
              <span className="text-white text-[11px] font-black tracking-widest pointer-events-none">{formatDateDisplay(endDate)}</span>
              <input
                type="date"
                value={endDate}
                onChange={e => { setEndDate(e.target.value); setFilterType('custom'); }}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </label>
          </div>
          <button onClick={() => setIsModalOpen(true)} className="bg-[#00ff66] text-[#0b0e11] font-black px-6 py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#0bb574] transition-all shadow-[0_0_20px_rgba(0,255,102,0.3)] active:scale-95 uppercase text-[11px] tracking-widest">
            <PlusCircle size={18} /> Nova Entrada
          </button>
        </div>
      </div>

      {filteredBets.length > 0 && <StatsCards bets={filteredBets} />}

      {filteredBets.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-[#1e2329] p-6 rounded-2xl border border-[#474d57] shadow-lg relative overflow-hidden group">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h3 className="text-lg font-black flex items-center gap-2 uppercase text-white">
                  <TrendingUp className="text-[#00ff66]" size={20} /> CURVA DE LUCRO (P/L)
                </h3>
                <div className="flex items-center bg-[#0b0e11] p-1 rounded-xl border border-[#474d57]">
                  {['day', 'week', 'month'].map((id) => (
                    <button key={id} onClick={() => handleSetFilter(id as any)} className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${filterType === id ? 'bg-[#00ff66] text-[#0b0e11]' : 'text-[#848e9c] hover:text-white'}`}>
                      {id === 'day' ? 'Hoje' : id === 'week' ? '7D' : '30D'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-[320px]"><ProfitChart bets={filteredBets} /></div>
            </div>
            <div className="bg-[#1e2329] p-6 rounded-[32px] border border-[#00ff66]/20 shadow-2xl flex flex-col">
              <OutcomePieChart bets={filteredBets} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
            <div className="bg-[#1e2329] p-6 rounded-2xl border border-[#474d57] shadow-lg">
              <h3 className="text-sm font-black uppercase text-white mb-6">Performance Mercados</h3>
              <div className="h-[250px]"><MarketBarChart data={marketPerformance.slice(0, 5).map(m => ({ name: m.name, profit: m.profit }))} /></div>
            </div>
            <div className="bg-[#1e2329] p-6 rounded-2xl border border-[#474d57] shadow-lg">
              <h3 className="text-sm font-black uppercase text-white mb-6">Performance Ligas</h3>
              <div className="h-[250px]"><LeagueBarChart data={leaguePerformance.slice(0, 5).map(l => ({ name: l.name, profit: l.profit }))} /></div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-32 bg-[#1e2329]/30 border border-[#474d57]/30 rounded-[48px] text-center">
          <SearchX size={64} className="text-[#474d57]" />
          <h3 className="text-xl font-black uppercase tracking-tight text-white mt-4">Nenhuma entrada registrada para hoje</h3>
          <div className="flex items-center bg-[#0b0e11] p-1 rounded-xl border border-[#474d57] mt-6">
            {['day', 'week', 'month'].map((id) => (
              <button key={id} onClick={() => handleSetFilter(id as any)} className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${filterType === id ? 'bg-[#00ff66] text-[#0b0e11]' : 'text-[#848e9c] hover:text-white'}`}>
                {id === 'day' ? 'Hoje' : id === 'week' ? '7D' : '30D'}
              </button>
            ))}
          </div>
        </div>
      )}

      <AddBetModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={onAddBet} state={state} />
    </div>
  );
};

export default Dashboard;
